import { useLazyQuery, useQuery } from '@apollo/client';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { graphql } from 'gql';
import { SortDirection } from 'gql/graphql';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { SnackbarContext } from './SnackbarProvider';

type Show = {
    id: string
    title: string
}

type Season = {
    id: string
    title: string
    totalVoices: number
}

type SeasonShowContextType = {
    shows: Show[] | null,
    seasons: Season[] | null,
    selectedShow: Show | null,
    selectedSeason: Season | null,
    setSelectedShow: (show: Show | null) => void,
    setSelectedSeason: (season: Season | null) => void,
}

const initialState = {
    shows: null,
    seasons: null,
    selectedShow: null,
    selectedSeason: null,
    setSelectedShow: () => {},
    setSelectedSeason: () => {},
} as SeasonShowContextType;

export const SeasonShowContext = createContext<SeasonShowContextType>(initialState);

const GetShows = graphql(`
    query GetShows_SeasonShowProvider($options: ShowOptions) {
        shows(options: $options) {
            id
            title
        }
    }`);

const GetSeasonFromShow = graphql(`
    query GetSeasonFromShow_SeasonShowProvider($where: ShowWhere, $options: SeasonOptions) {
        shows(where: $where) {
            seasons(options: $options) {
                id
                title
                totalVoices
            }
        }
    }`);

export function SeasonShowProvider(props: { children: React.ReactNode }) {
    const { session, fromCache } = useStytchMemberSession();
    const { error } = useContext(SnackbarContext);

    const [ trackChanges, setTrackChanges ] = useState(false);
    const [ selectedShow, setSelectedShow ] = useState<Show | null>(null);
    const [ selectedSeason, setSelectedSeason ] = useState<Season | null>(null);

    const { data: showsData } = useQuery(GetShows, {
        skip: !session || fromCache,
        variables:{
            options: { sort: [{ created: SortDirection.Asc }]},
        },
    });

    const [getSeasons, { data: seasonData }] = useLazyQuery(GetSeasonFromShow)

    useEffect(() => {
        if (!session || fromCache) return;

        const lastOrg = JSON.parse(localStorage.getItem('lastOrg') ?? 'null')
        if (lastOrg === session.organization_id) {
            const lastSelectedShow = JSON.parse(localStorage.getItem('selectedShow') ?? 'null')
            const lastSelectedSeason = JSON.parse(localStorage.getItem('selectedSeason') ?? 'null')

            if (lastSelectedShow) setSelectedShow(lastSelectedShow)
            if (lastSelectedSeason) setSelectedSeason(lastSelectedSeason)
        }
        setTrackChanges(true);
    }, [fromCache, session]);

    useEffect(() => {
        if (!trackChanges) return;
        localStorage.setItem('selectedShow', JSON.stringify(selectedShow))
        localStorage.setItem('selectedSeason', JSON.stringify(selectedSeason))
        localStorage.setItem('lastOrg', JSON.stringify(session?.organization_id ?? null))
    }, [selectedShow, selectedSeason, setSelectedShow, setSelectedSeason, session, trackChanges]);

    useEffect(() => {
        if (!selectedShow) return;
        getSeasons({
            variables: {
                where: { id: selectedShow?.id },
                options: { sort: [{ created: SortDirection.Asc }]},
            },
        }).catch(e => error(e.message))
    }, [selectedShow, getSeasons, error])

    const shows = showsData?.shows ?? null;
    const seasons = seasonData?.shows[0] ? seasonData.shows[0].seasons : null;

    return (
        <SeasonShowContext.Provider value={{
            shows,
            seasons,
            selectedShow,
            selectedSeason,
            setSelectedShow,
            setSelectedSeason,
        }}>
            {props.children}
        </SeasonShowContext.Provider>
    )
}